import { Edit3, Trash2, UserIcon } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as VehicleIcon } from "../../assets/icons/dash/Wheel.svg";
import { ReactComponent as MoneyIcon } from "../../assets/icons/moneys.svg";
import AppButton from "../../components/ui/AppButton";
import { Separator } from "../../components/ui/common";
import DashboardCard from "../../components/ui/dashboard/DashboardCard";
import { ActiveStatusBadge } from "../../components/ui/dashboard/StatusCommon";
import SkeletonForDetailsPage from "../../components/ui/SkeletonForDetailsPage";
import { buildApiUrl, formatDateDDMMYYYY } from "../../lib/utils";
import DeleteUserModal from "../user/ui/DeleteUserModal";
import { useGetPartnerByIdQuery } from "./partnerApiSlice";
import PartnerModal from "./PartnerModal";
import PartnerVehicleAndFinance from "./refactor/PartnerVehicleAndFinance";

const PartnerDetails = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState("");

  const { data, isLoading, isError, refetch } = useGetPartnerByIdQuery(
    id || "",
    {
      skip: !id,
    }
  );

  if (!id) {
    return <></>;
  }

  // Loading skeleton
  if (isLoading) {
    return <SkeletonForDetailsPage />;
  }

  if (isError || (!isLoading && !data)) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-500">Error loading partner details</div>
      </div>
    );
  }

  const partner = data?.data;

  if (!partner) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-500">Vehicle not found</div>
      </div>
    );
  }

  const handleModal = () => {
    setOpenModal("");
    refetch();
  };

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
          <button
            onClick={() => navigate("/admin/partners")}
            className="hover:text-gray-800"
          >
            All partners
          </button>
          <span>›</span>
          <span className="text-gray-800 font-medium">
            {partner?.fullName || "Partner details"}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-12 bg-white rounded-[10px] p-4 text-[16px]">
          <div className="flex items-center space-x-10">
            {partner.imageUrl ? (
              <img
                src={buildApiUrl(partner?.imageUrl)}
                alt={partner?.fullName}
                className="w-[8rem] h-[8rem] rounded-[10px] object-cover border border-gray-300 shadow-sm"
              />
            ) : (
              <div className="w-[8rem] h-[8rem] rounded-[10px] object-cover border border-gray-300 shadow-sm">
                <UserIcon className="w-full h-full text-hColor" />
              </div>
            )}
          </div>

          <div className="w-full space-y-4">
            <div className="w-full flex flex-col gap-4 lg:flex-row lg:items-center justify-between">
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <h1 className="font-semibold">{partner.fullName}</h1>
                  <ActiveStatusBadge isActive={partner.isActive} />
                </div>
                <h1 className="font-thin text-pColor text-[13px]">
                  {partner.email}
                </h1>
              </div>
              <div className="flex space-x-12">
                <div className="flex flex-col items-start">
                  <h1 className="font-thin text-pColor mb-1">Payout ID</h1>
                  <h1 className="font-semibold">{partner.payOutId}</h1>
                </div>
                <div className="flex flex-col items-start">
                  <h1 className="font-thin text-pColor mb-1">Phone number</h1>
                  <h1 className="font-semibold">{partner.phoneNumber}</h1>
                </div>
                <div className="flex flex-col items-start">
                  <h1 className="font-thin text-pColor mb-1">Home address</h1>
                  <h1 className="font-semibold">{partner.address}</h1>
                </div>
                <div className="flex flex-col items-start">
                  <h1 className="font-thin text-pColor mb-1">
                    Date registered
                  </h1>
                  <h1 className="font-semibold">
                    {formatDateDDMMYYYY(partner.createdOn)}
                  </h1>
                </div>
              </div>
            </div>

            <div className="h-[2px] w-full bg-gray-200"></div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => {}}
                className="flex items-center px-4 py-2 border border-pColor text-pColor rounded-lg h-[38px] text-sm"
              >
                Change status
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  className="h-6 w-6 ml-2"
                >
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="m7 10l5 5l5-5"
                  />
                </svg>
              </button>
              <button
                onClick={() => setOpenModal("edit")}
                className="flex items-center px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/60 hover:text-white transition-colors h-[38px] text-sm"
              >
                Edit details
                <Edit3 className="h-4 w-4 ml-2" />
              </button>

              <AppButton
                onClick={() => {}}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors h-[38px]"
              >
                Payout
              </AppButton>

              <AppButton
                onClick={() => setOpenModal("delete")}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-800 transition-colors h-[38px] text-sm"
              >
                Delete
                <Trash2 className="h-4 w-4 ml-2" />
              </AppButton>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4 bg-white rounded-[10px] p-4">
          <DashboardCard
            icon={<VehicleIcon className="h-8 w-8" />}
            value={isLoading ? "0" : partner.totalVehicle.toLocaleString()}
            label={"Vehicle leased"}
            className="w-[300px]"
          />

          <Separator />

          <DashboardCard
            icon={<MoneyIcon className="h-8 w-8" />}
            value={isLoading ? "0" : partner.totalEarning.toLocaleString()}
            label={"Total earnings"}
            className="w-[300px]"
          />

          <Separator />

          <DashboardCard
            icon={<MoneyIcon className="h-8 w-8" />}
            value={isLoading ? "0" : partner.pendingSettlement.toLocaleString()}
            label={"Pending settlement"}
            className="w-[300px]"
          />
        </div>

        <PartnerVehicleAndFinance partnerId={partner.id} />
      </div>

      <PartnerModal
        isOpen={openModal === "edit"}
        onClose={() => handleModal()}
        partner={partner}
      />
      <DeleteUserModal
        isOpen={openModal === "delete"}
        onClose={handleModal}
        userId={partner.userId}
        entityLabel="partner"
        redirectTo="/admin/partners"
      />
    </div>
  );
};

export default PartnerDetails;
